import {InMemoryCache} from '@apollo/client';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';

import type {Cache} from '@apollo/client';
import type {ValidTopLevelField} from 'src/internal/revamp/graphql/types';
import type {
  DataOfDoc,
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';
import type {ObservabilityFns} from 'src/internal/shared/observability/types';

export type WatchCleanupFn = () => void;

type WatchCallback<TDocumentNode extends GraphQlDocument<any, any, any, any>> =
  (data: DataOfDoc<TDocumentNode> | null) => void;

type WatchOptions<TDocumentNode extends GraphQlDocument<any, any, any, any>> = {
  query: TDocumentNode;
  variables: VariablesOfDoc<TDocumentNode> | undefined;
  callback: WatchCallback<TDocumentNode>;
};

type ReadQueryOptions<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> = {
  query: TDocumentNode;
  variables: VariablesOfDoc<TDocumentNode> | undefined;
};

type WriteQueryOptions<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> = ReadQueryOptions<TDocumentNode> & {
  data: DataOfDoc<TDocumentNode> | null;
};

type WriteMutationOptions<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> = {
  data: DataOfDoc<TDocumentNode> | null;
  mutation: TDocumentNode;
  variables: VariablesOfDoc<TDocumentNode> | undefined;
};

// Wrapper around Apollo's InMemoryCache to be able to switch to other
// implementations if needed. We should not leak any types coming over from
// apollo.
export default class MemoryCache {
  private cache: InMemoryCache;
  private observabilityFns: ObservabilityFns;

  constructor(observabilityFns: ObservabilityFns, cache?: InMemoryCache) {
    this.cache = cache || new InMemoryCache();
    this.observabilityFns = observabilityFns;
  }

  watch<TDocumentNode extends GraphQlDocument<any, any, any, any>>({
    query,
    variables,
    callback,
  }: WatchOptions<TDocumentNode>): WatchCleanupFn {
    const cleanup = this.cache.watch({
      query,
      variables,
      optimistic: true,
      callback: (diff: Cache.DiffResult<DataOfDoc<TDocumentNode>>) => {
        if (diff.complete) {
          callback(diff.result || null);
        }
      },
    });

    return cleanup;
  }

  readQuery<TDocumentNode extends GraphQlDocument<any, any, any, any>>({
    query,
    variables,
  }: ReadQueryOptions<TDocumentNode>): DataOfDoc<TDocumentNode> | null {
    try {
      return this.cache.readQuery<DataOfDoc<TDocumentNode>>({
        query,
        variables,
      });
    } catch (e: unknown) {
      reportRuntimeError(
        this.observabilityFns,
        e,
        'Could not perform cache.read operation',
      );

      throw new Error('Could not perform cache.read operation');
    }
  }

  writeQuery<TDocumentNode extends GraphQlDocument<any, any, any, any>>({
    query,
    variables,
    data,
  }: WriteQueryOptions<TDocumentNode>): void {
    this.cache.writeQuery({
      query,
      variables,
      data,
    });
  }

  writeMutation<TDocumentNode extends GraphQlDocument<any, any, any, any>>({
    mutation,
    variables,
    data,
  }: WriteMutationOptions<TDocumentNode>): void {
    this.cache.write({
      result: data,
      dataId: 'ROOT_MUTATION',
      query: mutation,
      variables,
    });

    // We have no use for the mutation result in the cache atm, so we remove it entirely
    // This might change when we get to optimistic updates
    this.cache.evict({id: 'ROOT_MUTATION'});
  }

  evictTopLevelFields(topLevelFields: ReadonlyArray<ValidTopLevelField>): void {
    topLevelFields.forEach((fieldName) => {
      this.cache.evict({
        id: 'ROOT_QUERY',
        fieldName,
      });
    });

    this.cache.gc();
  }
}
